import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Badge, Alert, Spinner, Button, Form } from 'react-bootstrap';
import {
  importBankStatement,
  getBankStatementImports,
  getPendingReconciliations,
  validateReconciliation,
  rejectReconciliation,
} from './DataService';

const STATUS_INFO = {
  pending: { label: 'En attente', bg: 'secondary' },
  processing: { label: 'Traitement…', bg: 'info' },
  done: { label: 'Terminé', bg: 'success' },
  failed: { label: 'Échec', bg: 'danger' },
};

const MATCH_TYPE_LABEL = {
  nouveau_paiement: 'Nouveau paiement',
  correction_date: 'Correction de date',
};

const formatEuros = (value) => `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const Rapprochement = () => {
  const [imports, setImports] = useState([]);
  const [proposed, setProposed] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedCandidate, setSelectedCandidate] = useState({});
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [importsData, pendingData] = await Promise.all([
        getBankStatementImports(),
        getPendingReconciliations(),
      ]);
      setImports(importsData || []);
      setProposed(pendingData.proposed || []);
      setUnmatched(pendingData.unmatched || []);
    } catch (err) {
      setError("Erreur lors du chargement des données de rapprochement");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  };

  const handleImport = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const result = await importBankStatement(file);
      if (result.success) {
        setSuccess("Import lancé — un email de récap sera envoyé une fois le traitement terminé.");
        setFile(null);
        fetchAll();
      }
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'import du fichier");
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleValidate = async (transaction) => {
    const numFacture = selectedCandidate[transaction.id] || transaction.proposed_num_facture;
    setProcessingId(transaction.id);
    setError(null);
    try {
      const result = await validateReconciliation(transaction.id, numFacture);
      if (result.success) {
        setSuccess(`Facture ${result.num_facture} mise à jour`);
        setProposed((prev) => prev.filter((t) => t.id !== transaction.id));
      }
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de la validation");
      console.error(err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (transaction) => {
    setProcessingId(transaction.id);
    setError(null);
    try {
      const result = await rejectReconciliation(transaction.id);
      if (result.success) {
        setProposed((prev) => prev.filter((t) => t.id !== transaction.id));
        setUnmatched((prev) => prev.filter((t) => t.id !== transaction.id));
      }
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors du rejet");
      console.error(err);
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Rapprochement bancaire</h2>
          <p className="text-muted">Importez un relevé bancaire (CSV) pour proposer des rapprochements avec les factures en compte</p>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      {/* Upload */}
      <Card className="mb-4">
        <Card.Body>
          <Form.Label>Relevé bancaire (CSV)</Form.Label>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className="d-flex flex-column align-items-center justify-content-center text-center p-4 mb-3"
            style={{
              border: `2px dashed ${isDragging ? '#0d6efd' : '#ced4da'}`,
              borderRadius: '0.375rem',
              backgroundColor: isDragging ? '#e7f1ff' : '#f8f9fa',
              cursor: 'pointer',
              transition: 'background-color 0.15s, border-color 0.15s',
            }}
          >
            <Form.Control
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={(e) => setFile(e.target.files[0] || null)}
              hidden
            />
            {file ? (
              <span>📄 {file.name}</span>
            ) : (
              <span className="text-muted">
                Glissez votre relevé CSV ici, ou cliquez pour parcourir
              </span>
            )}
          </div>
          <Button variant="primary" onClick={handleImport} disabled={!file || uploading}>
            {uploading ? <Spinner animation="border" size="sm" /> : 'Importer'}
          </Button>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : (
        <>
          {/* Imports passés */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="h6 text-uppercase text-muted mb-3">Imports</Card.Title>
              {imports.length === 0 ? (
                <p className="text-muted mb-0">Aucun import pour le moment</p>
              ) : (
                <Table striped hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Fichier</th>
                      <th>Statut</th>
                      <th>Relevé au</th>
                      <th>Rapprochées</th>
                      <th>Déjà importées</th>
                      <th>Non rapprochées</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imports.map((imp) => (
                      <tr key={imp.id}>
                        <td>{new Date(imp.created_at).toLocaleString('fr-FR')}</td>
                        <td>{imp.filename}</td>
                        <td>
                          <Badge bg={STATUS_INFO[imp.status]?.bg || 'secondary'}>
                            {STATUS_INFO[imp.status]?.label || imp.status}
                          </Badge>
                          {imp.status === 'failed' && imp.error_message && (
                            <div className="text-danger small mt-1">{imp.error_message}</div>
                          )}
                        </td>
                        <td>{imp.solde_date ? new Date(imp.solde_date).toLocaleDateString('fr-FR') : '—'}</td>
                        <td>{imp.nb_proposees}</td>
                        <td>{imp.nb_deja_importees}</td>
                        <td>{imp.nb_non_rapprochees}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* À valider */}
          <Card className="mb-4">
            <Card.Body>
              <Card.Title className="h6 text-uppercase text-muted mb-3">À valider ({proposed.length})</Card.Title>
              {proposed.length === 0 ? (
                <p className="text-muted mb-0">Rien à valider</p>
              ) : (
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Date valeur</th>
                      <th>Montant</th>
                      <th>Payeur / libellé</th>
                      <th>Proposition</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposed.map((t) => {
                      const candidates = t.alternative_candidates || [];
                      const hasChoice = candidates.length > 1;
                      const selectedNum = selectedCandidate[t.id] || t.proposed_num_facture;
                      const selectedCand = candidates.find((c) => String(c.num_facture) === String(selectedNum));
                      const matchType = selectedCand?.type || t.match_type;
                      const currentPaidAt = selectedCand?.paid_at;
                      return (
                        <tr key={t.id}>
                          <td>{new Date(t.date_valeur).toLocaleDateString('fr-FR')}</td>
                          <td>{formatEuros(t.montant)}</td>
                          <td>
                            <div>{t.payeur || '—'}</div>
                            <div className="text-muted small">{t.libelle}</div>
                          </td>
                          <td>
                            {hasChoice ? (
                              <Form.Select
                                size="sm"
                                value={selectedNum}
                                onChange={(e) =>
                                  setSelectedCandidate((prev) => ({ ...prev, [t.id]: e.target.value }))
                                }
                              >
                                {candidates.map((c) => (
                                  <option key={c.num_facture} value={c.num_facture}>
                                    #{c.num_facture} — {c.client_name} — {formatEuros(c.montant)} — {MATCH_TYPE_LABEL[c.type]}
                                    {c.type === 'correction_date' && c.paid_at ? ` (actuelle: ${new Date(c.paid_at).toLocaleDateString('fr-FR')})` : ''}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : (
                              <span>
                                Facture #{t.proposed_num_facture} — <Badge bg="info">{MATCH_TYPE_LABEL[t.match_type]}</Badge>
                              </span>
                            )}
                            {matchType === 'correction_date' && (
                              <div className="text-muted small mt-1">
                                Date actuelle : <strong>{currentPaidAt ? new Date(currentPaidAt).toLocaleDateString('fr-FR') : '—'}</strong>
                                {' → '}Date réelle (relevé) : <strong>{new Date(t.date_valeur).toLocaleDateString('fr-FR')}</strong>
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-success"
                                size="sm"
                                disabled={processingId === t.id}
                                onClick={() => handleValidate(t)}
                              >
                                ✅ Valider
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={processingId === t.id}
                                onClick={() => handleReject(t)}
                              >
                                ❌ Rejeter
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* Non rapprochées */}
          {unmatched.length > 0 && (
            <Card>
              <Card.Body>
                <Card.Title className="h6 text-uppercase text-muted mb-3">Non rapprochées ({unmatched.length})</Card.Title>
                <Table striped size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Date valeur</th>
                      <th>Montant</th>
                      <th>Payeur / libellé</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatched.map((t) => (
                      <tr key={t.id}>
                        <td>{new Date(t.date_valeur).toLocaleDateString('fr-FR')}</td>
                        <td>{formatEuros(t.montant)}</td>
                        <td>
                          <div>{t.payeur || '—'}</div>
                          <div className="text-muted small">{t.libelle}</div>
                        </td>
                        <td>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={processingId === t.id}
                            onClick={() => handleReject(t)}
                          >
                            🙈 Masquer
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </Container>
  );
};

export default Rapprochement;
